import React, {useEffect, useRef, useState} from 'react';
import {Document, Page, pdfjs} from 'react-pdf';
import {useNavigate, useParams} from 'react-router-dom';
import {Box, Button, CircularProgress, Paper, Stack, Typography} from '@mui/material';
import axios from "axios";
import {ReactComponent as ChevronLeftIcon} from "../../assets/icons/chevron-left.svg"
import {useTranslation} from 'react-i18next';
import {collectionService} from "../../services/collection.service";
import {EXTENSION_TO_FILE_TYPE, FILE_EXTENSION, FILE_TYPE_TO_MIME} from "../../models/collections.models";
import ImageViewer from "../ocr-batch/ImageViewer";
import DownloadFileView from "./DownloadFileView";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

interface FileViewerProps {
    fileId?: string;
    fileUrl?: string;
    fileName?: string;
}

const FILE_VIEW_CONFIG: Record<string, { titleKey: string; infoKey: string; defaultExtension: string }> = {
    xlsx: { titleKey: 'fileViewer.excelFile', infoKey: 'fileViewer.excelInfo', defaultExtension: 'xlsx' },
    docx: { titleKey: 'fileViewer.wordFile', infoKey: 'fileViewer.wordInfo', defaultExtension: 'docx' },
    pptx: { titleKey: 'fileViewer.pptFile', infoKey: 'fileViewer.pptInfo', defaultExtension: 'pptx' },
    rtf: { titleKey: 'fileViewer.rtfFile', infoKey: 'fileViewer.rtfInfo', defaultExtension: 'rtf' },
};

const FileViewer= ({fileId, fileUrl, fileName}: FileViewerProps) => {
    const {t} = useTranslation();
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [fileData, setFileData] = useState<string | null>(null);
    const [numPages, setNumPages] = useState<number | null>(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [error, setError] = useState('');

    const [fileExtension, setFileExtension] = useState<string | null>(null);
    const [textContent, setTextContent] = useState<string | null>(null);
    const [fileType, setFileType] = useState<FILE_EXTENSION | null>(null);

    const [imageArrayBuffer, setImageArrayBuffer] = useState<ArrayBuffer | null>(null);
    const [imageMimeType, setImageMimeType] = useState<string | null>(null);

    const [isLoading, setIsLoading] = useState(true);

    const config = fileType ? FILE_VIEW_CONFIG[fileType] : null;

    const objectUrlRef = useRef<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        const revokeCurrentUrl = () => {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
        };

        const resolveFileName = async (): Promise<string | null> => {
            if (fileName) return fileName;

            const finalFileId = fileId || id;
            if (!finalFileId) return null;

            const metadata = await collectionService.fetchDocument(Number(finalFileId));
            return metadata.name || metadata.fileName || null;
        };

        const fetchTextContent = async (): Promise<string> => {
            const finalFileId = fileId || id;
            const response = await axios.get(fileUrl || `/documents/${finalFileId}/download`, {
                responseType: 'text'
            });
            return response.data;
        };

        const fetchBlobUrl = async (mimeType: string): Promise<string> => {
            const finalFileId = fileId || id;
            const response = await axios.get(fileUrl || `/documents/${finalFileId}/download`, {
                responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: mimeType });
            return URL.createObjectURL(blob);
        };

        const fetchImageData = async (): Promise<{ buffer: ArrayBuffer; mimeType: string } | null> => {
            const finalFileId = fileId || id;
            const finalFileName = fileName || 'file';
            if (!finalFileId) return null;

            const file = await collectionService.getDocumentFile(Number(finalFileId), finalFileName);
            const buffer = await file.arrayBuffer();
            return { buffer, mimeType: file.type };
        };

        const markUnsupported = () => {
            setFileType('unsupported');
        };

        const resetState = () => {
            setFileData(null);
            setNumPages(null);
            setPageNumber(1);
            setImageArrayBuffer(null);
            setImageMimeType(null);
        };

        const loadFile = async () => {
            resetState();
            setIsLoading(true);

            try {
                const finalFileName = await resolveFileName();

                if (!finalFileName) {
                    markUnsupported();
                    return;
                }

                const extension = finalFileName.split('.').pop()?.toLowerCase();

                if (!extension) {
                    markUnsupported();
                    return;
                }

                setFileExtension(extension);

                const fileType = EXTENSION_TO_FILE_TYPE[extension];

                if (!fileType) {
                    markUnsupported();
                    return;
                }

                setFileType(fileType);

                if (fileType === 'txt') {
                    const text = await fetchTextContent();
                    if (!cancelled) setTextContent(text);
                    return;
                }

                const isImage = ['jpeg', 'jpg', 'png'].includes(fileType);
                if (isImage) {
                    const result = await fetchImageData();
                    if (!cancelled && result) {
                        setImageArrayBuffer(result.buffer);
                        setImageMimeType(result.mimeType);
                    }
                    return;
                }

                const mimeType = FILE_TYPE_TO_MIME[fileType] ?? 'application/octet-stream';
                const url = await fetchBlobUrl(mimeType);

                if (cancelled) {
                    URL.revokeObjectURL(url);
                    return;
                }

                revokeCurrentUrl(); // delete old url
                objectUrlRef.current = url;
                setFileData(url);
            } catch (err) {
                if (!cancelled) {
                    setFileType('error');
                    setError(t('fileViewer.loadError') as string);
                }
                console.error('File load error:', err);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        void loadFile();

        return () => {
            cancelled = true;
            revokeCurrentUrl();
        };
    }, [t, fileId, id, fileUrl, fileName]);

    const onDocumentLoadSuccess = ({numPages}: { numPages: number }) => {
        setNumPages(numPages);
    };

    const handleBack = () => {
        if (window.history.state && window.history.state.idx > 0) {
            navigate(-1);
        } else {
            navigate('/dashboard'); // fallback - zawsze gdzieś prowadzi
        }
    };

    const renderErrorView = () => (
        <div style={{padding: '20px', textAlign: 'center'}}>
            <Typography color="error" variant="h6">{error}</Typography>
            <Button
                variant="contained"
                onClick={() => navigate(-1)}
                sx={{mt: 2}}
            >
                {t('fileViewer.back')}
            </Button>
        </div>
    );

    const renderBackButton = () => (
        <Button
            startIcon={<ChevronLeftIcon/>}
            variant="text"
            onClick={handleBack}
        >
            {t('fileViewer.back')}
        </Button>
    );

    const renderLoadingView = () => <CircularProgress sx={{margin: '40px auto'}}/>;

    const renderUnsupportedView = () => (
        <Box sx={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
                {renderBackButton()}
            </Stack>

            <Paper variant="outlined" sx={{ borderRadius: '8px', padding: '40px' }}>
                <Typography variant="h6" color="error">
                    {t('fileViewer.unsupportedFormat')}: .{fileExtension}
                </Typography>
                <Typography variant="body1" sx={{ mt: 2 }}>
                    {t('fileViewer.supportedFormats')}
                </Typography>
            </Paper>
        </Box>
    );

    const renderTextView = () => (
        <Box sx={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
                {renderBackButton()}
            </Stack>

            <Paper
                variant="outlined"
                sx={{
                    borderRadius: '8px',
                    padding: '20px',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    maxHeight: '80vh',
                    overflow: 'auto',
                }}
            >
                {textContent}
            </Paper>
        </Box>
    );

    const renderDownloadView = () => {
        if (!config || !fileType) return renderUnsupportedView();
        const { titleKey, infoKey, defaultExtension } = config;

        return (
            <DownloadFileView
                titleKey={titleKey}
                infoKey={infoKey}
                defaultExtension={defaultExtension}
                fileData={fileData}
                fileName={fileName}
                fileExtension={fileType}
                renderBackButton={renderBackButton}
            />
        );
    };

    const renderImageView = () => (
        <Box sx={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
                {renderBackButton()}
            </Stack>

            <Paper variant="outlined" sx={{ borderRadius: '8px', overflow: 'hidden', height: '75vh' }}>
                <ImageViewer
                    data={imageArrayBuffer ?? undefined}
                    mimeType={imageMimeType ?? 'application/octet-stream'}
                />
            </Paper>
        </Box>
    );

    const renderPdfView = () => (
        <Box sx={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
                {renderBackButton()}

                <Stack direction="row" alignItems="center" sx={{ gap: '16px' }}>
                    <Button
                        disabled={pageNumber <= 1}
                        onClick={() => setPageNumber(p => p - 1)}
                        variant="outlined"
                    >
                        {t('fileViewer.previous')}
                    </Button>
                    <Typography>
                        {t('fileViewer.page')} {pageNumber} {t('fileViewer.of')} {numPages}
                    </Typography>
                    <Button
                        disabled={pageNumber >= (numPages || 0)}
                        onClick={() => setPageNumber(p => p + 1)}
                        variant="outlined"
                    >
                        {t('fileViewer.next')}
                    </Button>
                </Stack>
            </Stack>

            <Paper variant="outlined" sx={{ borderRadius: '8px', overflow: 'hidden' }}>
                <Document
                    file={fileData}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={(error) => {
                        setFileType('error');
                        setError(t('fileViewer.pdfLoadError') as string);
                        console.error('PDF load error:', error);
                    }}
                >
                    <Page
                        pageNumber={pageNumber}
                        width={window.innerWidth > 1200 ? 1200 : window.innerWidth - 40}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                    />
                </Document>
            </Paper>
        </Box>
    );

    if (isLoading) {
        return renderLoadingView();
    }

    switch (fileType) {
        case 'error':
            return renderErrorView();
        case 'unsupported':
            return renderUnsupportedView();
        case 'txt':
            return renderTextView();
        case 'docx':
        case 'xlsx':
        case 'pptx':
        case 'rtf':
            return renderDownloadView();
        case 'pdf':
            return renderPdfView();
        case 'jpeg':
        case 'png':
            return renderImageView();
        default:
            return renderUnsupportedView();
    }
};

export default FileViewer;